import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Checkbox } from '../../lib/shadcn/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { useBulkUpdateCustomers } from '../../hooks/backend/customersPage'
import { toast } from '../../lib/shadcn/sonner'
import type { DeliveryMethod } from './CustomerEditDialog'
import { reportStoreLocatorSync, type StoreLocatorSyncResult } from './storeLocatorSync'

// Fields that can be applied to every selected customer at once.
type TextFieldKey =
  | 'billing_city'
  | 'billing_state'
  | 'billing_zip'
  | 'shipping_city'
  | 'shipping_state'
  | 'shipping_zip'

const TEXT_FIELDS: Array<{ key: TextFieldKey; label: string }> = [
  { key: 'billing_city', label: 'Billing City' },
  { key: 'billing_state', label: 'Billing State' },
  { key: 'billing_zip', label: 'Billing Zip' },
  { key: 'shipping_city', label: 'Shipping City' },
  { key: 'shipping_state', label: 'Shipping State' },
  { key: 'shipping_zip', label: 'Shipping Zip' },
]

interface Props {
  open: boolean
  customerIds: string[]
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}

export default function CustomerBulkEditDialog({ open, customerIds, onOpenChange, onSaved }: Props) {
  const bulkUpdate = useBulkUpdateCustomers()
  const [saving, setSaving] = useState(false)

  // delivery_method toggle + value
  const [applyDelivery, setApplyDelivery] = useState(false)
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('shipping')

  // Web site store locator toggle + value
  const [applyStoreLocator, setApplyStoreLocator] = useState(false)
  const [storeLocator, setStoreLocator] = useState<'show' | 'hide'>('show')

  // Active / reorder reminders toggles + values
  const [applyActive, setApplyActive] = useState(false)
  const [active, setActive] = useState<'active' | 'inactive'>('inactive')
  const [applyReminders, setApplyReminders] = useState(false)
  const [reminders, setReminders] = useState<'include' | 'exclude'>('exclude')
  const [applyType, setApplyType] = useState(false)
  const [customerType, setCustomerType] = useState<'wholesale' | 'retail'>('retail')

  // text field toggles + values
  const [enabled, setEnabled] = useState<Record<TextFieldKey, boolean>>({
    billing_city: false,
    billing_state: false,
    billing_zip: false,
    shipping_city: false,
    shipping_state: false,
    shipping_zip: false,
  })
  const [textValues, setTextValues] = useState<Record<TextFieldKey, string>>({
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
  })

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (!open) return
    setApplyDelivery(false)
    setDeliveryMethod('shipping')
    setApplyStoreLocator(false)
    setStoreLocator('show')
    setApplyActive(false)
    setActive('inactive')
    setApplyReminders(false)
    setReminders('exclude')
    setApplyType(false)
    setCustomerType('retail')
    setEnabled({
      billing_city: false,
      billing_state: false,
      billing_zip: false,
      shipping_city: false,
      shipping_state: false,
      shipping_zip: false,
    })
    setTextValues({
      billing_city: '',
      billing_state: '',
      billing_zip: '',
      shipping_city: '',
      shipping_state: '',
      shipping_zip: '',
    })
  }, [open])

  const buildFields = () => {
    const fields: Record<string, string | boolean> = {}
    if (applyDelivery) fields['delivery_method'] = deliveryMethod
    if (applyStoreLocator) fields['store_locator'] = storeLocator === 'show'
    if (applyActive) fields['active'] = active === 'active'
    if (applyReminders) fields['reorder_reminders'] = reminders === 'include'
    if (applyType) fields['retail'] = customerType === 'retail'
    for (const { key } of TEXT_FIELDS) {
      if (enabled[key]) fields[key] = textValues[key]
    }
    return fields
  }

  const fieldsToApply = buildFields()
  const nothingSelected = Object.keys(fieldsToApply).length === 0

  const handleSave = async () => {
    if (customerIds.length === 0) {
      toast.error('No customers selected')
      return
    }
    if (nothingSelected) {
      toast.error('Enable at least one field to update')
      return
    }
    setSaving(true)
    try {
      const result = (await bulkUpdate.trigger({
        customer_ids: customerIds,
        fields: fieldsToApply,
      })) as { updated?: number; store_locator_sync?: StoreLocatorSyncResult | null } | null
      const count = result?.updated ?? customerIds.length
      toast.success(`Updated ${count} customer${count === 1 ? '' : 's'}`)
      reportStoreLocatorSync(result?.store_locator_sync)
      onSaved()
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update customers')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next && !saving) onOpenChange(false)
      }}
    >
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Edit Customers</DialogTitle>
          <DialogDescription>
            Enable a field and its value will be applied to all {customerIds.length} selected customer
            {customerIds.length === 1 ? '' : 's'}. Disabled fields are left unchanged.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Delivery method */}
          <div className="flex items-start gap-3 rounded-md border p-3">
            <Checkbox
              id="bulk-delivery"
              checked={applyDelivery}
              onCheckedChange={value => setApplyDelivery(!!value)}
              className="mt-1"
            />
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="bulk-delivery">Delivery Method</Label>
              <Select
                value={deliveryMethod}
                onValueChange={value => setDeliveryMethod(value as DeliveryMethod)}
                disabled={!applyDelivery}
              >
                <SelectTrigger id="bulk-delivery-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shipping">Shipping</SelectItem>
                  <SelectItem value="delivery">Delivery</SelectItem>
                  <SelectItem value="free_delivery">Free Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Web site store locator */}
          <div className="flex items-start gap-3 rounded-md border p-3">
            <Checkbox
              id="bulk-store-locator"
              checked={applyStoreLocator}
              onCheckedChange={value => setApplyStoreLocator(!!value)}
              className="mt-1"
            />
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="bulk-store-locator">Web Store Locator</Label>
              <Select
                value={storeLocator}
                onValueChange={value => setStoreLocator(value as 'show' | 'hide')}
                disabled={!applyStoreLocator}
              >
                <SelectTrigger id="bulk-store-locator-value">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="show">Show on the store locator</SelectItem>
                  <SelectItem value="hide">Remove from the store locator</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Syncs to the store locator on poppinwtx.com only, not B2B.
              </p>
            </div>
          </div>

          {/* Active */}
          <div className="flex items-start gap-3 rounded-md border p-3">
            <Checkbox
              id="bulk-active"
              checked={applyActive}
              onCheckedChange={value => setApplyActive(!!value)}
              className="mt-1"
            />
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="bulk-active">Status</Label>
              <Select
                value={active}
                onValueChange={value => setActive(value as 'active' | 'inactive')}
                disabled={!applyActive}
              >
                <SelectTrigger id="bulk-active-value">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Inactive customers never get reorder reminders.</p>
            </div>
          </div>

          {/* Wholesale or retail */}
          <div className="flex items-start gap-3 rounded-md border p-3">
            <Checkbox
              id="bulk-type"
              checked={applyType}
              onCheckedChange={value => setApplyType(!!value)}
              className="mt-1"
            />
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="bulk-type">Customer Type</Label>
              <Select
                value={customerType}
                onValueChange={value => setCustomerType(value as 'wholesale' | 'retail')}
                disabled={!applyType}
              >
                <SelectTrigger id="bulk-type-value">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wholesale">Wholesale</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Retail customers are hidden on the Customers page unless you show them, and never get reorder reminders.
              </p>
            </div>
          </div>

          {/* Reorder reminders */}
          <div className="flex items-start gap-3 rounded-md border p-3">
            <Checkbox
              id="bulk-reminders"
              checked={applyReminders}
              onCheckedChange={value => setApplyReminders(!!value)}
              className="mt-1"
            />
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="bulk-reminders">Reorder Reminders</Label>
              <Select
                value={reminders}
                onValueChange={value => setReminders(value as 'include' | 'exclude')}
                disabled={!applyReminders}
              >
                <SelectTrigger id="bulk-reminders-value">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="include">Include in reorder reminders</SelectItem>
                  <SelectItem value="exclude">Leave out of reorder reminders</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Text fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TEXT_FIELDS.map(({ key, label }) => (
              <div key={key} className="flex items-start gap-3 rounded-md border p-3">
                <Checkbox
                  id={`bulk-${key}`}
                  checked={enabled[key]}
                  onCheckedChange={value =>
                    setEnabled(prev => ({ ...prev, [key]: !!value }))
                  }
                  className="mt-1"
                />
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor={`bulk-${key}-input`}>{label}</Label>
                  <Input
                    id={`bulk-${key}-input`}
                    value={textValues[key]}
                    disabled={!enabled[key]}
                    onChange={e =>
                      setTextValues(prev => ({ ...prev, [key]: e.target.value }))
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving || nothingSelected}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Saving…
              </>
            ) : (
              `Apply to ${customerIds.length} customer${customerIds.length === 1 ? '' : 's'}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
